import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X,
  PlayCircle,
  CheckCircle,
  Lock,
  BookOpen
} from 'lucide-react';

const CourseViewer = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // TODO: Fetch course and lesson data
    setLoading(false);
    // Simulated data
    const mockCourse = {
      id,
      title: "Advanced Sales Techniques",
      sections: [
        {
          id: 1,
          title: "Introduction to Advanced Sales",
          lessons: [
            {
              id: "101",
              title: "Understanding Customer Psychology",
              duration: "45m",
              videoUrl: "https://example.com/video1.mp4",
              transcript: "Lesson transcript goes here...",
              isComplete: true,
              resources: [
                { id: 1, title: "Sales Psychology PDF", type: "pdf" },
                { id: 2, title: "Customer Personas Template", type: "xlsx" }
              ]
            },
            {
              id: "102",
              title: "Building Trust and Rapport",
              duration: "1h",
              videoUrl: "https://example.com/video2.mp4",
              transcript: "Lesson transcript goes here...",
              isComplete: false,
              resources: []
            }
          ]
        },
        {
          id: 2,
          title: "Advanced Negotiation Tactics",
          lessons: [
            {
              id: "201",
              title: "Price Negotiation Strategies",
              duration: "1h 15m",
              videoUrl: "https://example.com/video3.mp4",
              transcript: "Lesson transcript goes here...",
              isComplete: false,
              isLocked: true,
              resources: []
            }
          ]
        }
      ]
    };
    setCourse(mockCourse);

    const lessonId = searchParams.get('lesson') || mockCourse.sections[0].lessons[0].id;
    const lesson = mockCourse.sections
      .flatMap(section => section.lessons)
      .find(lesson => lesson.id === lessonId);
    setCurrentLesson(lesson);
  }, [id, searchParams]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!course || !currentLesson) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Not Found</h2>
          <p className="text-gray-600 mb-4">The lesson you're looking for doesn't exist.</p>
          <button 
            onClick={() => navigate(`/courses/${id}`)}
            className="text-blue-900 hover:underline"
          >
            Back to Course
          </button>
        </div>
      </div>
    );
  }

  const allLessons = course.sections.flatMap(section => section.lessons);
  const currentIndex = allLessons.findIndex(lesson => lesson.id === currentLesson.id);
  const previousLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  const handleLessonChange = (lessonId) => {
    navigate(`/courses/${id}/watch?lesson=${lessonId}`);
  };

  const handleComplete = async () => {
    // TODO: Mark lesson as complete in backend
    console.log('Marking lesson as complete:', currentLesson.id);
  };

  return (
    <div className="h-screen flex bg-gray-100">
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-80 bg-white transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:relative lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Course Content</h2>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {course.sections.map((section) => (
              <div key={section.id} className="border-b last:border-b-0">
                <div className="p-4 bg-gray-50">
                  <h3 className="font-medium text-gray-900">{section.title}</h3>
                </div>
                
                <div className="space-y-1 p-2">
                  {section.lessons.map((lesson) => (
                    <button
                      key={lesson.id}
                      onClick={() => !lesson.isLocked && handleLessonChange(lesson.id)}
                      disabled={lesson.isLocked}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                        lesson.id === currentLesson.id
                          ? 'bg-blue-50 text-blue-900'
                          : lesson.isLocked
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {lesson.isComplete ? (
                        <CheckCircle size={18} className="text-green-500 shrink-0" />
                      ) : lesson.isLocked ? (
                        <Lock size={18} className="text-gray-400 shrink-0" />
                      ) : (
                        <PlayCircle size={18} className="text-gray-400 shrink-0" />
                      )}
                      <span className="text-sm text-left">{lesson.title}</span>
                      <span className="text-xs text-gray-500 ml-auto">{lesson.duration}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Top Navigation */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu size={24} />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{course.title}</h1>
              <p className="text-sm text-gray-600">{currentLesson.title}</p>
            </div>
          </div>
          
          <button
            onClick={() => navigate(`/courses/${id}`)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Video Player */}
        <div className="flex-1 bg-gray-900 relative">
          {/* TODO: Implement actual video player */}
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <span className="text-lg">Video Player Placeholder</span>
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="bg-white border-t px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => previousLesson && handleLessonChange(previousLesson.id)}
            disabled={!previousLesson}
            className={`flex items-center gap-2 ${
              previousLesson
                ? 'text-gray-900 hover:text-blue-900'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <ChevronLeft size={20} />
            <span className="text-sm">Previous Lesson</span>
          </button>

          <button
            onClick={handleComplete}
            className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
          >
            <CheckCircle size={18} />
            Mark Complete
          </button>

          <button
            onClick={() => nextLesson && handleLessonChange(nextLesson.id)}
            disabled={!nextLesson}
            className={`flex items-center gap-2 ${
              nextLesson
                ? 'text-gray-900 hover:text-blue-900'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <span className="text-sm">Next Lesson</span>
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Lesson Details */}
        <div className="bg-white border-t overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="prose prose-blue max-w-none">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Lesson Resources
              </h2>
              
              {currentLesson.resources && currentLesson.resources.length > 0 ? (
                <div className="space-y-3">
                  {currentLesson.resources.map((resource) => (
                    <a
                      key={resource.id}
                      href="#"
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50"
                    >
                      <BookOpen size={18} className="text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{resource.title}</p>
                        <p className="text-sm text-gray-500 uppercase">{resource.type}</p>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No resources available for this lesson.</p>
              )}

              <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
                Transcript
              </h2>
              <div className="text-gray-600">
                {currentLesson.transcript}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseViewer;